// Style tags are not form facets on purpose: facet entries route through
// handleWithFilters, which disables the pack toggle, and the pack toggle must
// stay usable while a style is selected.
const STYLE_FACET_KEY = 'denim-style';
const STYLE_TAGS_CHANGED_EVENT = 'collection-filters:style-tags';

class CollectionFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.filterData = [];
      this.filterTitle = '';
      this.styleTags = [];
      this.entries = [];
      this.collectionProductsDataEn = [];
      this.domain = `${Shopify.routes.root}`;
      
      this.collectionHandle = this.dataset.collectionHandle;
      this.productsGrid = document.querySelector("#CollectionProductGrid #main-collection-product-grid");
      this.activeFacets = document.querySelectorAll(".custom-filter-active-facets");
      this.filterForm = this.querySelector("form");
      this.otherFilters = Array.from(document.querySelectorAll("collection-filters-form"))
                             .filter((filter) => !Object.is(filter, this));
      this.collectionGrid = document.querySelector('collection-product-grid');
      
      this.sectionSettings = JSON.parse(document.querySelector("#sectionSettings")?.innerHTML || "{}");
      
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
      this.onSubmitHandler = this.onSubmitHandler.bind(this);
      this.onTryOnFilter = this.onTryOnFilter.bind(this);
      this.toggleFilterOption = this.toggleFilterOption.bind(this);
      
      this.initializeEventListeners();
      this.loadCollectionData();
    }
  
    initializeEventListeners() {
      this.filterForm.addEventListener("input", this.onSubmitHandler);
      
      this.querySelectorAll(".try-on-filter-value").forEach(checkbox =>
        checkbox.addEventListener("click", this.onTryOnFilter)
      );
  
      this.querySelectorAll(".sidebar-filter-option-header").forEach(header =>
        header.addEventListener("click", this.toggleFilterOption)
      );

      document.querySelectorAll(".packs-filter").forEach(packFilter =>
        packFilter.addEventListener("click", () => this.togglePackFilter())
      );
    }
  
    async loadCollectionData() {
      try {
        const [collData, collDataEn] = await Promise.all([
          fetch(`${window.Shopify.routes.root}collections/${this.collectionHandle}/products.json?limit=500`).then(r => r.json()),
          fetch(`${window.Shopify.routes.root}collections/${this.collectionHandle}/products.json?limit=500`).then(r => r.json())
        ]);
  
        this.collectionProductsData = collData.products;
        this.filterData = collData.products;
        this.collectionProductsDataEn = collDataEn.products;
  
        await Promise.all(
          this.collectionProductsData
            .filter(product => product.title.toUpperCase().includes("PACK"))
            .map(product => this.getRedirects(product))
        );
  
        this.openActiveFilterHeaders();
        this.checkTryOnFilter();
        this.reconcileActiveFiltersOnLoad();
      } catch (error) {
        console.error('Error loading collection data:', error);
      }
    }

    reconcileActiveFiltersOnLoad() {
      // The grid's first paint comes from Shopify's own server-side filtering
      // (collection.products in the section), which doesn't share this file's
      // stock-aware, per-variant matching. If the page loaded with filters
      // already applied (shared link, bookmark, back/forward navigation) no
      // checkbox "input" event ever fires, so re-run the client-side filtering
      // once here to reconcile the grid with the corrected logic.
      const hasActiveFilters = this.filterForm.querySelector('input:checked');
      if (!hasActiveFilters) return;

      this.rerender();
    }
  
    async getRedirects(product) {
      try {
        const metafields = await fetch(`${window.Shopify.routes.root}products/${product.handle}?view=mb_price`).then(r => r.json());
        product.redirects = metafields.redirects?.items || [];
        return product;
      } catch (error) {
        console.error('Error getting redirects:', error);
        return product;
      }
    }
  
    toggleFilterOption(event) {
      const filterOption = event.target.closest(".sidebar-filter-option");
      const header = filterOption.querySelector(".sidebar-filter-option-header");
      const options = filterOption.querySelector(".sidebar-filter-values__wrapper");
      
      header.classList.toggle("has-active-filters");
      options.setAttribute("aria-expanded", 
        options.getAttribute("aria-expanded") === "true" ? "false" : "true"
      );
    }
  
    onTryOnFilter(event) {
      const selectedFilter = event.target.value;
      const checkboxes = document.querySelectorAll("collection-filters-form .try-on-filter-value");
      
      checkboxes.forEach(checkbox => {
        checkbox.checked = checkbox.value === selectedFilter;
      });
    }
  
    onSubmitHandler(event) {
      event.preventDefault();
      this.syncCollectionFilters();
      const formData = new FormData(event.target.closest("form"));
      const searchParams = new URLSearchParams(formData).toString();
      this.renderPage(searchParams, event, false);
    }
  
    syncCollectionFilters() {
      this.otherFilters.forEach(other => {
        const checkboxes = other.filterForm.querySelectorAll("input[type='checkbox']");
        checkboxes.forEach(checkbox => {
          const cbName = checkbox.getAttribute("name");
          const cbValue = checkbox.getAttribute("value");
          const matchingInput = this.querySelector(`input[name="${cbName}"][value="${cbValue}"]`);
          if (matchingInput) checkbox.checked = matchingInput.checked;
        });
      });
    }
  
    async renderPage(searchParams, event, updateURLHash = true) {
      const collection = document.getElementById("CollectionProductGrid").querySelector(".collection");
      collection.classList.add("loading");

      await this.renderSectionViaData(event);
      
      if (updateURLHash) this.updateURLHash(searchParams);
      
      const activeFacets = document.querySelector(".general-filters");
      if (activeFacets) {
        const headerOffset = 140;
        const elementPosition = activeFacets.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
      
      if (this.collectionGrid) {
        this.collectionGrid.initProducts = 12;
        this.collectionGrid.productsGrid.innerHTML = '';
        
        if (this.filterData.length === 0) {
          this.collectionGrid.productsGrid.innerHTML = `<h4 class="empty-collection">No products match the filter.</h4>`;
          const showProductsBtn = this.collectionGrid.querySelector(".render-products");
          if (showProductsBtn) {
            showProductsBtn.classList.add("hidden");
          }
        } else {
          const initialProducts = this.filterData.slice(0, 12);
          const initialProductsHTML = initialProducts
            .map(prod => ProductCardTemplate.newCardTemplate(prod))
            .join("");
          
          this.collectionGrid.productsGrid.innerHTML = initialProductsHTML;

          const showProductsBtn = this.collectionGrid.querySelector(".render-products");
          if (showProductsBtn) {
            if (this.filterData.length <= 12) {
              showProductsBtn.classList.add("hidden");
            } else {
              showProductsBtn.classList.remove("hidden");
              const span = showProductsBtn.querySelector("span");
              if (span) {
                span.innerHTML = `${this.filterData.length - 12}`;
              }
            }
          }
        }
      }

      this.updateQuickAddOptions();
      collection.classList.remove("loading");
    }
  
    updateQuickAddOptions() {
      document.querySelectorAll('.quick-add-options__wrapper').forEach(optionWrapper => {
        const productCard = optionWrapper.closest("product-card");
        const selectedHandle = productCard.getAttribute("data-selected-swatch");
        const quickAdd = productCard.querySelector("quick-add");

        if (quickAdd?.querySelectorAll('.quick-add-option').length === 1) {
          this.updateProductOptions(productCard, selectedHandle);
        }
      });
      // Re-attach hover listeners for every card in this render, not just
      // ones with a single option — <quick-add>'s own window "load"-gated
      // hover setup never fires for cards created after the initial page
      // load, so this is the only reliable way newly rendered quick-add
      // buttons get hover working after a filter change.
      this.enabledQuickAddBtn();
    }
  
    async updateProductOptions(productCard, selectedHandle) {
      try {
        const product = await fetch(`${window.Shopify.routes.root}products/${selectedHandle}.js`).then(r => r.json());
        
        productCard.querySelectorAll(".quick-add-option-value").forEach(option => {
          const position = option.parentElement.getAttribute("data-option-position");
          const value = option.textContent;
          const filterOptionPosition = position;
  
          const availableOptions = product.variants
            .filter(variant => variant[position] === value && variant.available)
            .map(variant => variant[filterOptionPosition]);
  
          option.classList.toggle("unavailable", !availableOptions.includes(option.textContent));
        });
  
        this.enabledQuickAddBtn();
      } catch (error) {
        console.error('Error updating product options:', error);
      }
    }
  
    async renderSectionViaData(event, form = null) {
      const collectionProductsData = this.collectionProductsData || 
        await fetch(`${window.Shopify.routes.root}collections/${this.collectionHandle}/products.json?limit=500`)
          .then(r => r.json())
          .then(data => data.products);
  
      this.mergeColorOptions(collectionProductsData);
  
      const formData = form ? new FormData(form) : new FormData(event.target.closest("form"));
      const { filterEntries, facetEntries } = this.processFormData(formData);
      
      this.entries = facetEntries;
      const packFilter = document.querySelector(".packs-filter");
  
      const onlyPacks = this.isPackFilterActive && facetEntries.length === 0;
      const productsToFilter = collectionProductsData.filter(product =>
        (!this.styleTags.length || this.matchesStyleTags(product)) &&
        (!onlyPacks || product.title.toLowerCase().includes("pack"))
      );
  
      if (Object.keys(filterEntries).length === 0) {
        this.handleNoFilters(productsToFilter, packFilter);
      } else {
        this.handleWithFilters(productsToFilter, filterEntries, packFilter);
      }
  
      this.renderFiltersViaData(facetEntries);
      this.updateProductCount();
    }
  
    mergeColorOptions(products) {
      products.forEach(prod => {
        const desiredObject = this.collectionProductsDataEn.find(obj => obj.id === prod.id);
        if (desiredObject) {
          const colorOption = prod.options.find(option => option.name === "Color" || option.name === "Couleur");
          const changeColor = desiredObject.options.find(option => option.name === "Color" || option.name === "Couleur");
          if (colorOption && changeColor) {
            colorOption.values.push(changeColor.values[0]);
          }
        }
      });
    }
  
    processFormData(formData) {
      const filterEntries = {};
      const facetEntries = [];
  
      for (const [key, value] of formData.entries()) {
        if (!filterEntries[key]) {
          filterEntries[key] = [value];
        } else {
          filterEntries[key].push(value);
        }
        facetEntries.push({ key, value });
      }
  
      return { filterEntries, facetEntries };
    }
  
    handleNoFilters(products, packFilter) {
      this.filterData = products;
  
      if (this.collectionGrid) {
        this.collectionGrid.isPackFilterActive = false;
      }
  
      if (packFilter) {
        packFilter.style.pointerEvents = "auto";
        packFilter.style.opacity = "1";
      }
  
      this.renderProducts(products.slice(0, 12));
      this.updateCollectionGrid(products);
    }
  
    handleWithFilters(products, filterEntries, packFilter) {
      if (packFilter) {
        packFilter.classList.remove("active");
        packFilter.style.pointerEvents = "none";
        packFilter.style.opacity = "0.5";
        this.isPackFilterActive = false;
      }
  
      const filteredProducts = this.applyFilters(products, filterEntries);
      this.filterData = filteredProducts;
      this.renderProducts(filteredProducts);
      this.updateCollectionGrid(filteredProducts);
    }
  
    applyFilters(products, filterEntries) {
      const filters = {
        size: this.getSizeFilteredProducts(products, filterEntries),
        color: this.getColorFilteredProducts(products, filterEntries),
        availability: this.getAvailabilityFilteredProducts(products, filterEntries),
        fit: this.getFitFilteredProducts(products, filterEntries)
      };

      if (filterEntries.colorgroup && filters.color.length === 0) {
        return [];
      }

      const finalFilteredProducts = products.filter(product => {
        const matchesAllFilters = Object.entries(filters).every(([filterType, filteredProducts]) => {
          if (filteredProducts.length === 0) return true;
          return filteredProducts.includes(product);
        });
        return matchesAllFilters;
      });

      return finalFilteredProducts;
    }
  
    getSizeFilteredProducts(products, filterEntries) {
      const sizeEntries = Object.entries(filterEntries)
        .filter(([key]) => key.includes("filter.v.option"));

      if (sizeEntries.length === 0) return products;

      // Each axis (Size/Waist/Inseam) is checked independently, and only
      // against products that actually carry that option — a tee has no
      // Waist option, a jean has no Size option, so a selected axis that
      // doesn't apply to a given product is skipped rather than excluding
      // it. A product must satisfy every axis that does apply to it, and at
      // least one selected axis must apply, with the matching value in stock.
      return products.filter(product => {
        let anyAxisApplies = false;
        const passesApplicableAxes = sizeEntries.every(([key, values]) => {
          const sizeType = key.split("filter.v.option.")[1]
            .replace(/\w\S*/g, w => w.replace(/^\w/, c => c.toUpperCase()));
          const option = product.options.find(opt => opt.name === sizeType);
          if (!option) return true;
          anyAxisApplies = true;
          return values.some(value =>
            product.variants.some(variant =>
              variant[`option${option.position}`] === value && variant.available
            )
          );
        });
        return anyAxisApplies && passesApplicableAxes;
      });
    }
  
    getColorFilteredProducts(products, filterEntries) {
      const colorEntries = Object.entries(filterEntries)
        .filter(([key]) => key.includes("colorgroup"));
      
      if (colorEntries.length === 0) return products;

      const filteredProducts = products.filter(product => {
        const hasColor = colorEntries.every(([_, values]) => 
          values.some(value => {
            const colorToMatch = value.toLowerCase();
            
            const matches = product.tags.some(tag => {
              if (tag.startsWith('colorgroup_')) {
                const tagColor = tag.replace('colorgroup_', '').toLowerCase();
                return tagColor === colorToMatch;
              }
              return false;
            });
            
            return matches;
          })
        );
        return hasColor;
      });
      
      return filteredProducts;
    }
  
    getAvailabilityFilteredProducts(products, filterEntries) {
      const availabilityEntries = Object.entries(filterEntries)
        .filter(([key]) => key.includes("filter.v.availability"));

      if (availabilityEntries.length === 0) return products;

      return products.filter(product =>
        availabilityEntries.every(([_, values]) => 
          values.some(value => {
            const productAvailability = product.variants.some(variant => variant.available);
            return value === "1" ? productAvailability : !productAvailability;
          })
        )
      );
    }
  
    getFitFilteredProducts(products, filterEntries) {
      const fitEntries = Object.entries(filterEntries)
        .filter(([key]) => key.includes("filter.p.m.filter.fit"));
      
      if (fitEntries.length === 0) return products;
  
      return products.filter(product => 
        fitEntries.every(([_, values]) => 
          values.some(value => product.tags.includes(`fit_${value}`))
        )
      );
    }
  
    renderProducts(products) {
      this.productsGrid.innerHTML = products.length > 0 
        ? products.map(prod => ProductCardTemplate.newCardTemplate(prod)).join("")
        : `<h1 class="empty-collection">No products match the filter.</h1>`;
    }
  
    updateCollectionGrid(products) {
      if (this.collectionGrid) {
        this.collectionGrid.initProducts = 12;
        this.collectionGrid.filterProducts = products;
      }
    }
  
    updateProductCount() {
      const qty_products = this.filterData.length;
      
      if (document.querySelector(".filter-title span")) {
        document.querySelector(".filter-title span").innerHTML = `${qty_products} X`;
      }

      document.querySelectorAll(".quick-size-filter__count-number").forEach(el => {
        el.innerHTML = qty_products;
      });
  
      const renderProducts = document.querySelector(".render-products");
      if (renderProducts) {
        if (qty_products > 12) {
          renderProducts.classList.remove("hidden");
          const span = renderProducts.querySelector("span");
          if (span) {
            span.innerHTML = `${qty_products - 12}`;
          }
        } else {
          renderProducts.classList.add("hidden");
        }
      }
    }
  
    renderFiltersViaData(facetEntries) {
      const sectionSettings = JSON.parse(document.querySelector("#sectionSettings")?.innerHTML || "{}");
      const filter_text = sectionSettings.filter_text || 'Filter by:';
      const styleEntries = this.styleTags.map(tag => ({ key: STYLE_FACET_KEY, value: tag }));
      const activeEntries = styleEntries.concat(facetEntries);
  
      const facetHTML = `
        ${activeEntries.length > 0 ? `<span class="mob-filter-facets-header">${filter_text}</span>` : ""}
        <span class="desk-filter-facets-header">${filter_text}</span>
        ${activeEntries.map(entry => this.createFacetHTML(entry, sectionSettings)).join("")}
        ${activeEntries.length > 0 ? `
          <facet-remove class="active-facets__button-wrapper" data-facet-type="clear">
            <a href="#" class="underlined-link">
              <span>${sectionSettings.clear_all}</span>
            </a>
          </facet-remove>
        ` : ""}
      `;
  
      this.activeFacets.forEach(el => el.innerHTML = facetHTML);
    }
  
    createFacetHTML(entry, sectionSettings) {
      const value = this.getFacetValue(entry);
      return `
        <facet-remove data-filter-label="${entry.key}" data-filter-value="${entry.value}">
          <a href="#" class="active-facets__button active-facets__button--light">
            <span class="active-facets__button-inner button button--tertiary">
              ${value}
              <svg aria-hidden="true" focusable="false" role="presentation" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.48627 9.32917L2.82849 3.67098" stroke="#333030" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2.88539 9.38504L8.42932 3.61524" stroke="#333030" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </a>
        </facet-remove>
      `;
    }
  
    getFacetValue(entry) {
      if (entry.key === STYLE_FACET_KEY) {
        return this.styleTitles().get(entry.value) || entry.value;
      }
      if (entry.key === "colorgroup") {
        return `<span class="filter-color-swatch ${ProductCardTemplate.handleize(entry.value)}"></span>`;
      }
      if (entry.key === "filter.p.m.filter.fit") {
        return entry.value.toUpperCase();
      }
      if (entry.key === "filter.v.availability") {
        return `<span class="filter-avaiability-swatch ${ProductCardTemplate.handleize(entry.value)}">
          ${entry.value === "1" ? "TRY-ON" : "NO TRY-ON"}
        </span>`;
      }
      return `${entry.key.split("filter.v.option.")[1].toUpperCase()}: ${entry.value}`;
    }
  
    enabledQuickAddBtn() {
      document.querySelectorAll('.quick-add-btn').forEach(btn => {
        const optionsWrapper = btn.querySelector('.quick-add-options__wrapper');
        if (btn) {
          btn.classList.remove('disabled');
          btn.addEventListener('mouseenter', () => optionsWrapper.style.display = 'block');
          btn.addEventListener('mouseleave', () => optionsWrapper.style.display = 'none');
        }
      });
    }
  
    openActiveFilterHeaders() {
      const filterOptions = document.querySelectorAll(".sidebar-filter-option");
  
      filterOptions.forEach((option) => {
        option
          .querySelectorAll("input:not(.mobile-filter-inputs)")
          .forEach((input) => {
            if (input.checked) {
              option
                .querySelector(".sidebar-filter-option-header")
                .classList.add("has-active-filters");
  
              option
                .querySelector(".sidebar-filter-values__wrapper")
                .setAttribute("aria-expanded", "true");
              return;
            }
          });
      });
    }
  
    checkTryOnFilter() {
      const searchParams = location.search.substring(1).split("&");
  
      searchParams.forEach((param) => {
        if (param.includes("try-on-filter")) {
          const pair = param.split("=");
          document
            .querySelectorAll(`input[value=${pair[1]}]`)
            .forEach((checkbox) => {
              checkbox.checked = true;
            });
          document
            .querySelector(`facet-remove[${pair[1]}]`)
            .classList.add("active");
        }
      });
    }
  
    onActiveFilterClick(event) {
      event.preventDefault();
      this.renderPage(new URL(event.currentTarget.href).searchParams.toString());
    }

    unToggleFromFacet(filterLabel, filterValue) {
      if (filterLabel === STYLE_FACET_KEY) {
        this.styleTags = this.styleTags.filter(tag => tag !== filterValue);
        this.dispatchStyleTagsChanged();
        return;
      }

      const checkbox = this.querySelector(`input[name="${filterLabel}"][value="${filterValue}"]`);
      if (checkbox) checkbox.checked = false;
    }

    clearInputs() {
      this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(input => {
        input.checked = false;
      });

      this.filterData = this.collectionProductsData;
      this.styleTags = [];
      this.dispatchStyleTagsChanged();

      if (this.collectionGrid) {
        this.collectionGrid.filterProducts = this.collectionProductsData;
        this.collectionGrid.initProducts = 12;
        this.collectionGrid.productsGrid.innerHTML = '';
        
        const initialProducts = this.collectionProductsData.slice(0, 12);
        const initialProductsHTML = initialProducts.length > 0 
          ? initialProducts.map(prod => ProductCardTemplate.newCardTemplate(prod)).join("")
          : `<h4 class="empty-collection">No products match the filter.</h4>`;
        
        this.collectionGrid.productsGrid.innerHTML = initialProductsHTML;

        const showProductsBtn = this.collectionGrid.querySelector(".render-products");
        if (showProductsBtn) {
          if (this.collectionProductsData.length <= 12) {
            showProductsBtn.classList.add("hidden");
          } else {
            showProductsBtn.classList.remove("hidden");
            const span = showProductsBtn.querySelector("span");
            if (span) {
              span.innerHTML = `${this.collectionProductsData.length - 12}`;
            }
          }
        }
      }

      this.renderFiltersViaData([]);

      document.querySelectorAll(".quick-size-filter__count-number").forEach(el => {
        el.innerHTML = this.collectionProductsData.length;
      });

      this.querySelectorAll(".sidebar-filter-option-header").forEach(header => {
        header.classList.remove("has-active-filters");
      });

      this.querySelectorAll(".sidebar-filter-values__wrapper").forEach(wrapper => {
        wrapper.setAttribute("aria-expanded", "false");
      });

      this.updateURLHash('');
    }

    updateURLHash(searchParams) {
      const url = new URL(window.location.href);
      if (searchParams) {
        url.search = searchParams;
      } else {
        url.search = '';
      }
      window.history.replaceState({}, '', url);
    }

    // OR semantics: any selected style matches.
    matchesStyleTags(product) {
      return product.tags.some(tag => this.styleTags.includes(tag.toLowerCase()));
    }

    toggleStyleTag(tag) {
      const normalized = tag.toLowerCase();
      const isSelected = this.styleTags.includes(normalized);
      this.setStyleTags(isSelected ? this.styleTags.filter(selected => selected !== normalized) : [...this.styleTags, normalized]);
    }

    setStyleTags(tags) {
      this.styleTags = tags.map(tag => tag.toLowerCase());
      this.dispatchStyleTagsChanged();
      this.rerender();
    }

    dispatchStyleTagsChanged() {
      document.dispatchEvent(new CustomEvent(STYLE_TAGS_CHANGED_EVENT, { detail: { tags: this.styleTags } }));
    }

    // Badge labels come from the banner buttons that expose the tags.
    styleTitles() {
      if (!this._styleTitles) {
        this._styleTitles = new Map(
          Array.from(document.querySelectorAll(".denim-filter-item[data-tag], .filter-item[data-tag]"))
            .map(button => [button.dataset.tag.toLowerCase(), button.dataset.title])
        );
      }
      return this._styleTitles;
    }

    // Banners decide whether packs apply to the selected style; the form owns the toggle.
    setPackFilterVisible(showPacks) {
      const packFilter = document.querySelector(".packs-filter");
      if (!packFilter) return;

      packFilter.classList.toggle("hidden", !showPacks);
      if (showPacks) return;
      packFilter.classList.remove("active");
      this.isPackFilterActive = false;
    }

    togglePackFilter() {
      this.isPackFilterActive = !this.isPackFilterActive;
      document.querySelectorAll(".packs-filter").forEach(packFilter =>
        packFilter.classList.toggle("active", this.isPackFilterActive)
      );
      this.rerender();
    }

    // Re-applies the current form state; the URL is left untouched.
    rerender() {
      this.renderPage('', { target: this.filterForm }, false);
    }
  }
  
  customElements.define('collection-filters-form', CollectionFiltersForm);
